import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft, FileText, Eye, Archive, Link2, Unlink, Loader2, CheckSquare, Square, Clock,
} from "lucide-react";
import { api, formatApiError } from "../api/client";
import { useAuth } from "../store/auth";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { toast } from "sonner";
import DocViewModal from "../components/DocViewModal";
import StepGuidance from "../components/StepGuidance";
import DeliverableBlock from "../components/DeliverableBlock";
import OwnerPersonSelect from "../components/OwnerPersonSelect";
import StepAssistant from "../components/StepAssistant";
import { STEP_STATUS } from "./Journey";

const DOC_STATUS_LABELS = { draft: "Borrador", review: "En revisión", approved: "Aprobado", archived: "Archivado", obsolete: "Obsoleto" };
const DOC_STATUS_CLS = {
  draft: "bg-slate-100 text-slate-700",
  review: "bg-amber-100 text-amber-800 border-amber-200",
  approved: "bg-emerald-100 text-emerald-800 border-emerald-200",
  archived: "bg-indigo-100 text-indigo-700 border-indigo-200",
  obsolete: "bg-slate-100 text-slate-400 line-through",
};

// Microcopy en lenguaje de personas
const STATUS_ACTIONS = {
  available: [["in_progress", "Empezar"], ["postponed", "Lo dejo para luego"], ["not_applicable", "Esto no va con mi empresa"]],
  in_progress: [["completed", "Lo doy por hecho"], ["postponed", "Lo dejo para luego"], ["not_applicable", "Esto no va con mi empresa"]],
  postponed: [["available", "Retomar"]],
  not_applicable: [["available", "Retomar"]],
  completed: [["available", "Reabrir"]],
};

export default function JourneyStep() {
  const { code } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const canWrite = user?.role !== "auditor";
  const canCreateDoc = !["auditor", "member"].includes(user?.role);

  const [detail, setDetail] = useState(null);
  const [users, setUsers] = useState([]);
  const [busy, setBusy] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [notes, setNotes] = useState("");
  const [viewDocId, setViewDocId] = useState(null);
  const [reasonDialog, setReasonDialog] = useState(null);
  const [reasonText, setReasonText] = useState("");
  const [linkOpen, setLinkOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/journey/steps/${code}?framework=iso27001`);
      setDetail(data);
      setNotes(data.state?.notes || "");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      navigate("/ruta");
    }
  }, [code, navigate]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/journey/steps/${code}?framework=iso27001`);
        if (cancelled) return;
        setDetail(data);
        setNotes(data.state?.notes || "");
      } catch (err) {
        if (!cancelled) { toast.error(formatApiError(err.response?.data?.detail)); navigate("/ruta"); }
      }
    })();
    return () => { cancelled = true; };
  }, [code, navigate]);
  useEffect(() => { if (canWrite) api.get("/users").then((r) => setUsers(Array.isArray(r.data) ? r.data : (r.data?.items || []))).catch(() => {}); }, [canWrite]);

  if (!detail) return <div className="text-slate-500">Cargando paso...</div>;

  const { phase, step, state, linked_documents: docs, controls_metrics, deliverable_status, custom_tasks } = detail;
  const guidance = step.guidance_structured || {};
  const humanTitle = step.human_title || step.title;

  const changeStatus = async (status, extra = {}) => {
    setBusy(true);
    try {
      const { data } = await api.put(`/journey/steps/${code}?framework=iso27001`, { status, ...extra });
      setDetail(data); setNotes(data.state?.notes || "");
      toast.success("Paso actualizado.");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const onStatusAction = (status) => {
    if (status === "postponed") { setReasonDialog({ status, label: "¿Por qué lo dejas para luego?", field: "postponed_reason" }); setReasonText(""); }
    else if (status === "not_applicable") { setReasonDialog({ status, label: "¿Por qué esto no va con tu empresa?", field: "not_applicable_justification" }); setReasonText(""); }
    else changeStatus(status);
  };

  const confirmReason = () => {
    if (!reasonText.trim()) return toast.error("Escribe un motivo, aunque sea breve.");
    changeStatus(reasonDialog.status, { [reasonDialog.field]: reasonText.trim() });
    setReasonDialog(null);
  };

  const toggleTask = async (taskCode, done) => {
    try {
      const { data } = await api.put(`/journey/steps/${code}/tasks/${taskCode}?framework=iso27001`, { done });
      setDetail(data);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const toggleCustomTask = async (id, done) => {
    try { const { data } = await api.put(`/journey/steps/${code}/custom-tasks/${id}?framework=iso27001`, { done }); setDetail(data); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const deleteCustomTask = async (id) => {
    try { const { data } = await api.delete(`/journey/steps/${code}/custom-tasks/${id}?framework=iso27001`); setDetail(data); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const saveOwner = (owner_person_id) => changeStatus(undefined, { owner_person_id: owner_person_id || "" });

  const saveNotes = async () => {
    setBusy(true);
    try {
      const { data } = await api.put(`/journey/steps/${code}?framework=iso27001`, { notes });
      setDetail(data); toast.success("Notas guardadas.");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const generateDeliverable = async () => {
    setGenerating(true);
    try {
      const { data } = await api.post(`/journey/steps/${code}/deliverable/draft?framework=iso27001`, {}, { timeout: 120000 });
      setDetail(data.step);
      toast.success("Borrador generado y vinculado al paso. Revísalo y, si te encaja, apruébalo.");
      if (data.document?.id) navigate(`/documents/${data.document.id}`);
    } catch (err) {
      // La IA puede tardar y el proxy cortar la conexión aunque el servidor sí complete.
      // Reintentamos leer el paso: si ya hay un borrador vinculado, lo abrimos.
      try {
        const { data: fresh } = await api.get(`/journey/steps/${code}?framework=iso27001`);
        const draft = (fresh.linked_documents || []).find((d) => ["draft", "review"].includes(d.status));
        if (draft) {
          setDetail(fresh);
          toast.success("Borrador generado. Ábrelo para revisarlo.");
          navigate(`/documents/${draft.id}`);
          return;
        }
      } catch { /* ignorar */ }
      toast.error(formatApiError(err.response?.data?.detail) || "La generación está tardando. Espera unos segundos y vuelve a intentarlo.");
    } finally { setGenerating(false); }
  };

  const archiveDoc = async (docId) => {
    try { await api.post(`/documents/${docId}/archive`); toast.success("Documento archivado."); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const unlinkDoc = async (docId) => {
    if (!window.confirm("¿Desvincular este documento del paso?")) return;
    try { await api.delete(`/journey/steps/${code}/link-document/${docId}?framework=iso27001`); toast.success("Documento desvinculado."); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const statusActions = STATUS_ACTIONS[state.status] || [];
  const isLocked = state.status === "locked";
  const legacyOwner = !state.owner_person_id && state.owner_user_id ? users.find((u) => u.id === state.owner_user_id)?.name : null;

  return (
    <div data-testid="journey-step-page" className="max-w-5xl">
      <Button variant="ghost" onClick={() => navigate("/ruta")} className="mb-3"><ArrowLeft className="h-4 w-4 mr-1" /> Ruta de implementación</Button>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_300px] gap-6">
        <div>
          <div className="text-[12px] text-slate-400">{phase.code} · {phase.title}</div>
          <div className="flex items-start gap-3 mt-1">
            <h1 className="text-[22px] font-semibold tracking-tight text-slate-900" data-testid="step-human-title">{humanTitle}</h1>
            <Badge className={`${(STEP_STATUS[state.status] || STEP_STATUS.available).cls} shrink-0 mt-1`}>{(STEP_STATUS[state.status] || STEP_STATUS.available).label}</Badge>
          </div>
          <div className="mt-1 flex flex-wrap items-center gap-2 text-[12px] text-slate-400">
            <span data-testid="step-formal-title">{step.code} · {step.title}</span>
            {step.clause_refs?.map((c) => <span key={c} className="border border-slate-200 rounded px-1.5 py-0.5">Cláusula {c}</span>)}
          </div>

          {guidance.summary_plain && (
            <p className="text-[15px] text-slate-700 mt-3" data-testid="step-summary-plain">{guidance.summary_plain}</p>
          )}
          {guidance.estimated_effort && (
            <div className="mt-2 inline-flex items-center gap-1.5 text-[13px] text-slate-500" data-testid="step-effort">
              <Clock className="h-3.5 w-3.5" /> {guidance.estimated_effort}
            </div>
          )}
          {guidance.example && (
            <div className="mt-3 bg-sky-50 border border-sky-100 rounded-md p-3 text-[13px] text-sky-900" data-testid="step-example">
              <span className="font-medium">Ejemplo: </span>{guidance.example}
            </div>
          )}

          {isLocked && (
            <div className="mt-4 bg-slate-50 border border-slate-200 rounded-md p-4 text-[13px] text-slate-500" data-testid="step-locked-note">
              Este paso está bloqueado. Antes tienes que terminar: {(step.hard_prerequisites || []).join(", ") || "los pasos previos"}.
            </div>
          )}

          {/* Entregable del paso (el SoA de solo lectura se muestra aunque el paso esté bloqueado) */}
          {(!isLocked || step.deliverable?.mode === "existing_artifact") && (
            <DeliverableBlock
              step={{ ...step, code }} status={deliverable_status} canCreateDoc={canCreateDoc}
              navigate={navigate} onGenerate={generateDeliverable} generating={generating}
              onChanged={load}
            />
          )}

          {/* Guía en lenguaje llano (divulgación progresiva) */}
          <StepGuidance guidance={guidance} />

          {/* Métricas de controles (grupos del Anexo A) */}
          {controls_metrics && (
            <div className="mt-5 bg-white border border-slate-200 rounded-md p-4" data-testid="step-controls-metrics">
              <div className="flex items-center justify-between mb-2">
                <span className="text-[13px] font-medium text-slate-700">Implementación de controles</span>
                <span className="text-[16px] font-semibold text-slate-900">{controls_metrics.percentage}%</span>
              </div>
              <div className="h-2 bg-slate-100 rounded overflow-hidden">
                <div className="h-2 bg-primary rounded" style={{ width: `${controls_metrics.percentage}%` }} />
              </div>
              <div className="text-[12px] text-slate-500 mt-2">{controls_metrics.implemented} de {controls_metrics.applicable} controles aplicables implementados.</div>
            </div>
          )}

          {/* Tareas */}
          {step.tasks?.length > 0 && (
            <div className="mt-5">
              <div className="text-[12px] uppercase tracking-wide text-slate-400 mb-2">Tareas</div>
              <div className="bg-white border border-slate-200 rounded-md divide-y divide-slate-100">
                {step.tasks.map((t) => {
                  const ts = (state.task_states || []).find((x) => x.task_code === t.code) || {};
                  const disabled = !canWrite || ["locked", "postponed", "not_applicable"].includes(state.status);
                  return (
                    <button key={t.code} disabled={disabled} onClick={() => toggleTask(t.code, !ts.done)}
                      className={`w-full flex items-center gap-3 px-4 py-2.5 text-left ${disabled ? "cursor-not-allowed opacity-70" : "hover:bg-slate-50"}`}
                      data-testid={`task-${t.code}`}>
                      {ts.done ? <CheckSquare className="h-4 w-4 text-emerald-600" /> : <Square className="h-4 w-4 text-slate-300" />}
                      <span className={`text-[14px] ${ts.done ? "text-slate-400 line-through" : "text-slate-800"}`}>{t.title}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Tareas personalizadas (de tu plan de acción) */}
          {custom_tasks?.length > 0 && (
            <div className="mt-5" data-testid="custom-tasks">
              <div className="text-[12px] uppercase tracking-wide text-slate-400 mb-2">Tu plan de acción</div>
              <div className="bg-white border border-slate-200 rounded-md divide-y divide-slate-100">
                {custom_tasks.map((t) => (
                  <div key={t.id} className="flex items-start gap-3 px-4 py-2.5" data-testid={`custom-task-${t.id}`}>
                    <button disabled={!canWrite} onClick={() => toggleCustomTask(t.id, !t.done)} className="mt-0.5" data-testid={`custom-task-toggle-${t.id}`}>
                      {t.done ? <CheckSquare className="h-4 w-4 text-emerald-600" /> : <Square className="h-4 w-4 text-slate-300" />}
                    </button>
                    <div className="flex-1">
                      <span className={`text-[14px] ${t.done ? "text-slate-400 line-through" : "text-slate-800"}`}>{t.title}</span>
                      {t.estimated_effort && <span className="text-[11px] text-slate-400"> · {t.estimated_effort}</span>}
                      {t.detail && <p className="text-[12px] text-slate-500">{t.detail}</p>}
                    </div>
                    {t.source === "ai_plan" && <Badge variant="outline" className="text-[10px] text-slate-400">IA</Badge>}
                    {canWrite && <button onClick={() => deleteCustomTask(t.id)} className="text-slate-300 hover:text-red-500 text-[16px] leading-none" data-testid={`custom-task-del-${t.id}`}>×</button>}
                  </div>
                ))}
              </div>
              <p className="text-[11px] text-slate-400 mt-1.5">Estas acciones las marcas tú. La IA no las completa.</p>
            </div>
          )}

          {/* Asistente IA por paso */}
          {!isLocked && (
            <StepAssistant stepCode={code} canWrite={canWrite} onTasksAdded={(d) => setDetail(d)} />
          )}

          {/* Documentos vinculados */}
          <div className="mt-5">
            <div className="flex items-center justify-between mb-2">
              <span className="text-[12px] uppercase tracking-wide text-slate-400">Documentos vinculados</span>
              {canCreateDoc && (
                <Button variant="outline" size="sm" onClick={() => setLinkOpen(true)} data-testid="step-link-doc"><Link2 className="h-3.5 w-3.5 mr-1" /> Vincular existente</Button>
              )}
            </div>
            <div className="bg-white border border-slate-200 rounded-md">
              {docs.length === 0 && <div className="px-4 py-6 text-center text-[13px] text-slate-400" data-testid="step-no-docs">Aún no hay documentos vinculados a este paso.</div>}
              {docs.map((d) => (
                <div key={d.id} className="flex items-center gap-3 px-4 py-2.5 border-b border-slate-100 last:border-0" data-testid={`linked-doc-${d.id}`}>
                  <FileText className="h-4 w-4 text-slate-400" />
                  <button className="flex-1 text-[14px] text-slate-800 text-left hover:text-primary" onClick={() => navigate(`/documents/${d.id}`)}>{d.title}</button>
                  <Badge className={DOC_STATUS_CLS[d.status]}>{DOC_STATUS_LABELS[d.status]}</Badge>
                  <span className="text-[11px] text-slate-400">v{d.current_version}</span>
                  <Button variant="ghost" size="sm" onClick={() => setViewDocId(d.id)} data-testid={`consultar-${d.id}`}><Eye className="h-4 w-4" /></Button>
                  {canWrite && d.status === "approved" && (
                    <Button variant="ghost" size="sm" onClick={() => archiveDoc(d.id)} title="Archivar" data-testid={`archive-${d.id}`}><Archive className="h-4 w-4 text-indigo-500" /></Button>
                  )}
                  {canWrite && (
                    <Button variant="ghost" size="sm" onClick={() => unlinkDoc(d.id)} title="Desvincular" data-testid={`unlink-${d.id}`}><Unlink className="h-4 w-4 text-slate-400" /></Button>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Panel lateral */}
        <div className="space-y-4">
          {canWrite && !isLocked && statusActions.length > 0 && (
            <div className="bg-white border border-slate-200 rounded-md p-4">
              <div className="text-[12px] uppercase tracking-wide text-slate-400 mb-2">Estado del paso</div>
              <div className="space-y-2">
                {statusActions.map(([s, label]) => (
                  <Button key={s} size="sm" variant={s === "completed" ? "default" : "outline"} className="w-full" disabled={busy} onClick={() => onStatusAction(s)} data-testid={`status-action-${s}`}>{label}</Button>
                ))}
              </div>
              {state.postponed_reason && <p className="text-[12px] text-slate-500 mt-3">Lo dejaste para luego: {state.postponed_reason}</p>}
              {state.not_applicable_justification && <p className="text-[12px] text-slate-500 mt-3">No va con tu empresa: {state.not_applicable_justification}</p>}
            </div>
          )}

          {canWrite && (
            <div className="bg-white border border-slate-200 rounded-md p-4">
              <div className="text-[12px] uppercase tracking-wide text-slate-400 mb-2">Responsable</div>
              <OwnerPersonSelect value={state.owner_person_id || ""} onChange={saveOwner} legacyUserLabel={legacyOwner} testid="step-owner-select" />
            </div>
          )}

          <div className="bg-white border border-slate-200 rounded-md p-4">
            <div className="text-[12px] uppercase tracking-wide text-slate-400 mb-2">Notas</div>
            {canWrite ? (
              <>
                <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={4} placeholder="Tus notas sobre este paso..." data-testid="step-notes" />
                <Button size="sm" variant="outline" className="mt-2 w-full" onClick={saveNotes} disabled={busy} data-testid="step-save-notes">Guardar notas</Button>
              </>
            ) : (
              <p className="text-[13px] text-slate-600 whitespace-pre-wrap">{state.notes || "—"}</p>
            )}
          </div>
        </div>
      </div>

      {viewDocId && <DocViewModal docId={viewDocId} onClose={() => setViewDocId(null)} />}

      <Dialog open={!!reasonDialog} onOpenChange={(o) => !o && setReasonDialog(null)}>
        <DialogContent data-testid="reason-dialog">
          <DialogHeader><DialogTitle>{reasonDialog?.label}</DialogTitle></DialogHeader>
          <Textarea value={reasonText} onChange={(e) => setReasonText(e.target.value)} rows={3} data-testid="reason-text" />
          <DialogFooter>
            <Button variant="ghost" onClick={() => setReasonDialog(null)}>Cancelar</Button>
            <Button onClick={confirmReason} data-testid="reason-confirm">Confirmar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {linkOpen && <LinkDocModal stepCode={code} linkedIds={docs.map((d) => d.id)} onClose={() => setLinkOpen(false)} onLinked={() => { setLinkOpen(false); load(); }} />}
    </div>
  );
}

function LinkDocModal({ stepCode, linkedIds, onClose, onLinked }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      try { const { data } = await api.get("/documents?page_size=100"); setItems((data.items || []).filter((d) => !linkedIds.includes(d.id))); }
      catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
      finally { setLoading(false); }
    })();
  }, [linkedIds]);

  const link = async (docId) => {
    try { await api.post(`/journey/steps/${stepCode}/link-document?framework=iso27001`, { document_id: docId }); toast.success("Documento vinculado."); onLinked(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="link-doc-modal" className="max-w-lg">
        <DialogHeader><DialogTitle>Vincular documento existente</DialogTitle></DialogHeader>
        <div className="max-h-80 overflow-y-auto -mx-1 px-1">
          {loading && <div className="py-6 text-center text-slate-500"><Loader2 className="h-4 w-4 animate-spin inline mr-1" /> Cargando...</div>}
          {!loading && items.length === 0 && <p className="text-[13px] text-slate-400 py-6 text-center">No hay documentos disponibles para vincular.</p>}
          {items.map((d) => (
            <button key={d.id} onClick={() => link(d.id)} className="w-full flex items-center gap-2 px-3 py-2 hover:bg-slate-50 rounded text-left" data-testid={`link-option-${d.id}`}>
              <FileText className="h-4 w-4 text-slate-400" />
              <span className="flex-1 text-[14px] text-slate-800">{d.title}</span>
              <span className="text-[11px] text-slate-400">{d.status}</span>
            </button>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
